import { useEffect } from 'react';

/**
 * Warns before leaving a page with unsaved work (review fix 2026-09-10).
 *
 * - Reload, tab close and external navigation: the browser's own `beforeunload` prompt (its
 *   wording is fixed by the browser).
 * - In-app navigation: this app uses react-router's <BrowserRouter> (a non-data router), where
 *   `useBlocker` isn't available, and converting the whole route tree to createBrowserRouter would
 *   touch every route. Instead, a capture-phase click listener on `document` asks for confirmation
 *   before any same-origin link (every NavLink/Link renders an <a>) moves to a different URL; on
 *   "Cancel" it stops the click before React Router's own handler ever sees it.
 * - Not intercepted: the browser Back/Forward buttons inside the app. Callers should keep their
 *   unsaved state somewhere that survives in-app navigation (see state/journalDraft.ts) so that
 *   path doesn't lose anything either.
 */
export function useLeaveWarning(active: boolean, message: string): void {
  useEffect(() => {
    if (!active) return;

    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Required by some browsers to show the prompt; the text itself is ignored.
      e.returnValue = '';
    };

    const onClickCapture = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const target = e.target instanceof Element ? e.target : null;
      const anchor = target?.closest('a[href]') as HTMLAnchorElement | null;
      if (!anchor || (anchor.target && anchor.target !== '_self') || anchor.hasAttribute('download')) return;
      const url = new URL(anchor.href, window.location.href);
      if (url.origin !== window.location.origin) return; // full page load -- beforeunload covers it
      if (url.pathname === window.location.pathname && url.search === window.location.search) return;
      if (!window.confirm(message)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    document.addEventListener('click', onClickCapture, true);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('click', onClickCapture, true);
    };
  }, [active, message]);
}
